import { useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, STATUS_BADGE, initialsOf } from "@/lib/utils";
import { useLang } from "@/i18n";

const sportLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "Football");

export default function AthleteScopeHeader({ athlete, onClear }) {
  const { t, tf } = useLang();
  const navigate = useNavigate();

  if (!athlete) {
    return <Skeleton className="h-[64px] rounded-xl" data-testid="athlete-scope-skeleton" />;
  }

  const meta = [sportLabel(athlete.sport), athlete.age ? `${athlete.age} ${tf("ans")}` : null, athlete.club]
    .filter(Boolean).join(" · ");

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-2.5 animate-fade-in motion-reduce:animate-none"
      data-testid="athlete-scope-header"
    >
      {athlete.photo_url ? (
        <img src={athlete.photo_url} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
      ) : (
        <span className="h-10 w-10 rounded-full bg-athlynx/15 border border-athlynx/30 text-athlynx text-xs font-bold flex items-center justify-center shrink-0">
          {initialsOf(athlete.name)}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate" data-testid="athlete-scope-name">{athlete.name}</span>
          {athlete.status && (
            <span className={cn("text-[10px] px-2 py-0.5 rounded-full border font-medium", STATUS_BADGE[athlete.status])}
              data-testid="athlete-scope-status">
              {t(`status.${athlete.status}`)}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{meta}</p>
      </div>
      <Button variant="outline" size="sm" className="h-8 text-xs shrink-0 hidden sm:inline-flex"
        onClick={() => navigate(`/players/${athlete.id}`)} data-testid="athlete-scope-profile-btn">
        {tf("Voir la fiche")} <ArrowRight className="h-3 w-3 ml-1" />
      </Button>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0 sm:hidden shrink-0"
        aria-label={tf("Voir la fiche")}
        onClick={() => navigate(`/players/${athlete.id}`)} data-testid="athlete-scope-profile-btn-mobile">
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground shrink-0"
        aria-label={tf("Retour au portefeuille")} onClick={onClear} data-testid="athlete-scope-clear">
        <X className="h-4 w-4" />
      </Button>
      <p className="sr-only" role="status">{tf("Dashboard filtré sur")} {athlete.name}</p>
    </div>
  );
}
